import { useMutation, useQuery } from "@tanstack/react-query";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { User, useSetUser, useUser } from "../contexts/UserContext";
import { useSetUserRegistration } from "../contexts/UserRegistrationContext";
import { _axios } from "../utils/axios";
import { cookieUtils } from "../utils/cookieUtils";

export function usePatchUserProfile() {
  const setUserRegistration = useSetUserRegistration();
  const user = useUser();
  return useQuery({
    enabled: false,
    queryKey: ["logged-in-user-profile"],
    queryFn: () =>
      _axios
        .post("/user-details/details", {
          user_id: user?.user_id,
        })
        .then((details) => {
          if (!details?.data?.data?.[0]) return null;
          
          // Store user registration details in secure cookie
          cookieUtils.setSecureCookie(
            'userRegistrationDetails', 
            details?.data?.data?.[0],
            { expires: 7 }
          );
          
          setUserRegistration(details?.data?.data?.[0]);
          return details?.data?.data?.[0];
        }),
  });
}

export function useUpdateProfile() {
  const setUser = useSetUser();
  const patch = usePatchUserProfile();
  return useMutation({
    /** BEFORE — email: string. Applicants may clear their email, sent as null. */
    mutationFn: (data: { email: string | null; mobile: string; name: string }) =>
      _axios.post<{ success: true; data: User }>("/users/update-profile", data),
    onSettled: () => {
      patch.refetch();
    },
    onError: (error: any) => {
      ErrorToast(
        error?.response?.data?.message ??
          "Something went wrong while updating info!"
      );
    },
    onSuccess: (result) => {
      if (result.data?.data?.user_id) {
        setUser(result.data.data);
        
        // Store user details in secure cookie
        cookieUtils.setSecureCookie(
          'userDetails', 
          result.data.data,
          { expires: 7 }
        );
        
        SuccessToast("Profile Updated!");
      }
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      _axios.post<{ success: true }>("/users/change-password", data),
    onError: () => ErrorToast("Something went wrong while changing password!"),
    onSuccess: (result) => {
      if (result.data?.success) SuccessToast("Password updated successfully!");
    },
  });
}
